import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { setStaffPassword, formatApiError } from "@/lib/api";

export default function SetPasswordPage() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        if (password !== confirm) { toast.error("Passwords don't match"); return; }
        setBusy(true);
        try {
            await setStaffPassword({ token, password });
            setDone(true);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-cream flex items-center justify-center px-4">
            <div className="w-full max-w-sm rounded-2xl border border-stone-300 bg-white p-8 shadow-xl" data-testid="set-password-page">
                <img src="/logo.png" alt="GhumneChaley.com" className="h-10 w-auto" />
                {done ? (
                    <div className="mt-6 text-center" data-testid="set-password-success">
                        <p className="font-display font-black text-xl text-navy">Password set!</p>
                        <p className="mt-2 text-sm text-stone-500">Your account is ready. Log in with your email and new password.</p>
                        <Link to="/admin" data-testid="set-password-login-link"
                            className="mt-5 inline-block rounded-full bg-saffron px-6 py-2.5 text-xs font-black uppercase tracking-wider text-white hover:brightness-95">
                            Go to Admin Login
                        </Link>
                    </div>
                ) : !token ? (
                    <p className="mt-6 text-sm text-red-600" data-testid="set-password-invalid">This invite link is missing its token — ask the Super Admin to resend your invite.</p>
                ) : (
                    <form onSubmit={submit} className="mt-6 space-y-4">
                        <h1 className="font-display font-black text-xl text-navy">Set your password</h1>
                        <p className="text-xs text-stone-500">Welcome to the GhumneChaley Admin Portal. Choose a password to activate your account.</p>
                        <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                            placeholder="New password (min 8 characters)" data-testid="set-password-input"
                            className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-saffron" />
                        <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                            placeholder="Confirm password" data-testid="set-password-confirm"
                            className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-saffron" />
                        <button type="submit" disabled={busy} data-testid="set-password-submit"
                            className="w-full rounded-full bg-saffron py-2.5 text-xs font-black uppercase tracking-wider text-white hover:brightness-95 disabled:opacity-50">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Set password & activate"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
